import { Injectable } from '@angular/core';

import { LoggingService } from '../../logging/logic/logging.service';
import { ActionHandler, ActionHandlerType } from '../action-handler';
import { ActivityConfirmationsService } from '../../activity-confirmations/logic/activity-confirmations.service';

/**
 * KRK-45: the answer to a queued draftdiscard.
 *
 * The backend answers OK even when it could not apply the deletion - a colleague took the draft
 * over meanwhile, it was submitted or locked, the AC is gone. Reporting that as an HTTP error
 * would be worse than the refusal itself: a permanently failing queued request stays at the head
 * of the FIFO queue and blocks everything behind it, form uploads included. So the outcome
 * travels in the payload and is evaluated here.
 *
 * On success there is nothing to do - the local row was removed when the user confirmed.
 *
 * On a refusal the local hiding has to be taken back: CloudDraftDeleteService marks the draft as
 * consumed BEFORE sending, so a sync in between cannot offer it again. Without undoing that, a
 * document that still exists for everybody else would stay invisible on this device for good.
 */
@Injectable({
  providedIn: 'root',
})
export class DraftDiscardedActionHandler implements ActionHandler {
  public readonly name: ActionHandlerType = 'draftdiscarded';

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private activityConfirmationsService: ActivityConfirmationsService,
  ) {}

  // ActionHandler
  public async executeAction(params?: any): Promise<any> {
    const discarded = this.toBoolean(params?.['discarded']);
    const reason = params?.['reason'];
    // camelCase, so the merged response (all lowercase keys) cannot overwrite it
    const acId = params?.['discardedAcId'] != undefined ? +params['discardedAcId'] : undefined;

    if (discarded) {
      this.logger.log(
        this.constructor.name,
        'draft deleted on the backend - acId',
        acId,
        'chainRootAcId',
        params?.['chainrootacid'],
        'versions',
        params?.['deletedacids'],
      );
      return;
    }

    this.logger.warn(
      this.constructor.name,
      'the backend did not apply the deletion of acId',
      acId,
      '- reason',
      reason,
      '- making the draft visible again',
    );

    if (acId != undefined && !isNaN(acId)) {
      await this.activityConfirmationsService.unmarkDraftConsumed(acId);
    }
  }

  // Private Functions
  /** The payload carries a real boolean, a legacy flat key could carry the string "true" */
  private toBoolean(value: any): boolean {
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true' || value === '1';
    }
    return Boolean(value);
  }
}
